import {
  Dialog,
  Typography,
} from "@mui/material";
import Box from "../../../components-dev/BoxExtended";
import { getSizing } from "../../../sizing";
import { useEffect, useState, useMemo } from "react";
import XNGButton from "../../../low-level/button";
import { DistrictAccessRequest, ServiceProviderType } from "../../../../profile-sdk";
import useApiQueryGetServiceProviderTypesByDate from "../../../../api/hooks/state_snapshots/use_api_query_get_service_provider_types_by_date";
import { MSBInputErrorWrapper, MSBSearchMultiselect } from "../../../../fortitude";
import msbMUIAutoCompleteFilterOptions from "../../../../utils/msb_mui_auto_complete_filter_options";
import { GetNotificationsDistrictAccessRequestNotification } from "@xng/xng-backend";
import { fetchUserResponse } from "../../../../api/fetchUserResponse";
import { getAvailableRoleOptions, isRoleOptionDisabled } from "../../../../utils/xlogs_role_mapper";
import { isAnyMsb } from "../../../../utils/msb_role";
import { useXNGSelector } from "../../../../context/store";
import { selectLoggedInClientAssignment, selectUser } from "../../../../context/slices/userProfileSlice";

interface IApproveDistrictAccessModal {
  handleDone: (roleLabels: string[], selectedServiceProviders: ServiceProviderType[]) => void;
  districtAccessRequest?: GetNotificationsDistrictAccessRequestNotification | null;
  setShowApproveDistrictAccessModal: (show: boolean) => void;
  showApproveDistrictAccessModal: boolean;
  stateInUs: string;
  serviceProviderTypes: ServiceProviderType[];
}

// (helper removed) Defaulting logic now uses `every`/`everySelectedAreAssistant` checks

async function getUserserviceProviderTypes(
  userId: string,
  stateInUs: string,
  userEmail?: string,
): Promise<ServiceProviderType[]> {
  const response = await fetchUserResponse(userId, userEmail);
  return response.serviceProviderTypes || [];
}

export function ApproveDistrictAccessModal(props: Readonly<IApproveDistrictAccessModal>) {
  const requestingUser = props.districtAccessRequest?.requestingUser;

  const currentUser = useXNGSelector(selectUser);
  const currentUserClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const isMSBEmployee = isAnyMsb(currentUser);
  const availableRoles = useMemo(
    () => getAvailableRoleOptions(isMSBEmployee, currentUserClientAssignment),
    [isMSBEmployee, currentUserClientAssignment],
  );

  const defaultToAssistantRoleList = useMemo(
    () => [
      "Nursing - LNA (Licensed Nursing Assistant)",
      "Occupational Therapist - Certified Assistant (COTA)",
      "State Licensed Occupational Therapist Assistant (COTA)",
      "State Licensed Physical Therapist Assistant (PTA)",
      "Physical Therapy - Licensed Assistant (LPTA)",
      "RBT (Registered Behavior Technician) / ABA Technician",
      "Rehabilitative Assistant",
      "Speech Language Pathologist - Licensed Assistant (SLPA)",
      "Case Manager or Special Education Teacher",
      "Paraeducator",
      "Certified Nursing Assistant (CNA)",
      "Licensed Professional Counselor Intern",
      "Licensed Specialist in School Psychology (LSSP) Intern",
      "State Licensed Audiology Assistant",
      "State Licensed Speech/Language Assistant",
      "State Licensed Speech/Language Pathology Intern",
    ],
    [],
  );
  const [userServiceProviderTypes, setUserServiceProviderTypes] = useState<ServiceProviderType[]>([]);
  const [shouldDefaultToAssistant, setShouldDefaultToAssistant] = useState(false);

  useEffect(() => {
    async function fetchServiceProviderTypes() {
      if (requestingUser?.id && props.stateInUs && requestingUser.email) {
        const types = await getUserserviceProviderTypes(requestingUser.id, props.stateInUs, requestingUser.email);
        setUserServiceProviderTypes(types);
        // Default to Assistant only if ALL service provider types are assistant types
        const allAreAssistant = types.length > 0 && types.every((type) => type.name && defaultToAssistantRoleList.includes(type.name));
        setShouldDefaultToAssistant(allAreAssistant);
        if (allAreAssistant) {
          setSelectedRoleLabels(["Assistant"]);
        }
      }
    }
    fetchServiceProviderTypes();
  }, [requestingUser?.id, requestingUser?.email, props.stateInUs, defaultToAssistantRoleList]);

  const [selectedRoleLabels, setSelectedRoleLabels] = useState<string[]>(
    shouldDefaultToAssistant ? ["Assistant"] : ["Autonomous"],
  );

  const {
    data: serviceProviderTypesOptionsResponse,
    isError: serviceProviderTypesOptionsIsError,
    isPending: serviceProviderTypesOptionsIsPending,
    refetch: refetchMutlipleServiceProviderTypesOptions,
  } = useApiQueryGetServiceProviderTypesByDate({
    queryParams: {
      state: props.stateInUs,
    },
  });

  const [selectedServiceProviderTypes, setSelectedServiceProviderTypes] = useState<
    ServiceProviderType[]
  >(userServiceProviderTypes);

  useEffect(() => {
    setSelectedServiceProviderTypes(userServiceProviderTypes);
  }, [userServiceProviderTypes]);

  return (
    <Dialog
      className="noselect"
      onClose={() => {
        props.setShowApproveDistrictAccessModal(false);
      }}
      open={props.showApproveDistrictAccessModal}
      PaperProps={{ sx: { minWidth: 500, overflow: "visible" } }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: getSizing(3),
          paddingTop: getSizing(3),
          paddingBottom: getSizing(2),
          paddingX: getSizing(2),
        }}
      >
        <Typography variant="h6">Please Select Provider Role</Typography>
        <Typography variant="body1">
          Please select the appropriate role for the provider you would like to approve.
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {requestingUser?.firstName} {requestingUser?.lastName}:
        </Typography>
        <Box
          display="inline-flex"
          sx={{
            gap: getSizing(1),
            alignItems: "center",
          }}
        >
          <Typography variant="body1" sx={{ textDecoration: "underline" }}>
            Service Provider Type:
          </Typography>
          <MSBInputErrorWrapper
            isError={serviceProviderTypesOptionsIsError}
            displayRefreshButton={serviceProviderTypesOptionsIsError}
            errorText={"Error fetching service provider types Options"}
            refetch={refetchMutlipleServiceProviderTypesOptions}
            rootProps={{
              sx: {
                flex: 1,
              },
            }}
          >
            <MSBSearchMultiselect
              selectedOptions={selectedServiceProviderTypes}
              options={serviceProviderTypesOptionsResponse?.serviceProviderTypes || []}
              getOptionLabel={(option) => `${option?.name}`}
              onChange={(newServiceProviderTypes, e) => {
                e?.preventDefault();
                e?.stopPropagation();
                setSelectedServiceProviderTypes(newServiceProviderTypes);

                const selectedServiceProviderTypeNames = newServiceProviderTypes.map((type) => type.name!);
                const allSelectedAreAssistant =
                  selectedServiceProviderTypeNames.length > 0 &&
                  selectedServiceProviderTypeNames.every((name) => defaultToAssistantRoleList.includes(name));
                if (allSelectedAreAssistant) {
                  setSelectedRoleLabels(["Assistant"]);
                } else {
                  setSelectedRoleLabels(["Autonomous"]);
                }
              }}
              renderOptionVariant="checkbox"
              variant="no overflow after 1"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              label={(() => {
                if (serviceProviderTypesOptionsIsPending) {
                  return "Loading Service Provider Types Options...";
                }
                if (serviceProviderTypesOptionsIsError) {
                  return "Failed to load Service Provider Types Options";
                }
                return "Service Provider Type(s)";
              })()}
              disabled={serviceProviderTypesOptionsIsPending || serviceProviderTypesOptionsIsError}
              autocompleteProps={{
                disableCloseOnSelect: true,
                filterOptions: msbMUIAutoCompleteFilterOptions(),
                onClick: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                },
              }}
              textFieldProps={{
                onClick: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                },
              }}
              sx={{
                flex: 1,
                maxWidth: "100%",
              }}
            />
          </MSBInputErrorWrapper>
        </Box>
        <Box
          display="inline-flex"
          sx={{
            gap: getSizing(1),
            alignItems: "center",
          }}
        >
          <Typography variant="body1" sx={{ textDecoration: "underline" }}>
            X Logs Role(s) Assigned:
          </Typography>
          <Box sx={{ position: "relative", minWidth: getSizing(15), flex: 1 }}>
            <MSBSearchMultiselect
              selectedOptions={selectedRoleLabels}
              options={availableRoles}
              getOptionLabel={(option) => option ?? ""}
              onChange={(newRoles) => {
                setSelectedRoleLabels(newRoles);
              }}
              renderOptionVariant="checkbox"
              variant="no overflow after 1"
              isOptionEqualToValue={(option, value) => option === value}
              label="X Logs Role(s)"
              disableSelectAll={true}
              autocompleteProps={{
                disableCloseOnSelect: true,
                fullWidth: true,
                filterOptions: msbMUIAutoCompleteFilterOptions(),
                getOptionDisabled: (option) => isRoleOptionDisabled(option, selectedRoleLabels),
                size: "small",
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            justifyContent: "center",
          }}
        >
          <XNGButton
            disabled={selectedServiceProviderTypes.length === 0 || selectedRoleLabels.length === 0}
            sx={{
              width: getSizing(6),
            }}
            onClick={() => {
              props.setShowApproveDistrictAccessModal(false);
              props.handleDone(selectedRoleLabels, selectedServiceProviderTypes);
            }}
          >
            Done
          </XNGButton>
        </Box>
      </Box>
    </Dialog>
  );
}
